import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Package, ShoppingCart, ArrowLeft, Loader2, ChevronLeft, ChevronRight, Share2, Store } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';
import CheckoutModal from '../components/CheckoutModal';
import ShareModal from '../components/ShareModal';
import { useBrand } from '../context/BrandContext';

const ProductPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const brand = useBrand();

  const brandName = brand?.name || 'MerchLike';
  const studioRoute = brand?.routes?.studio || '/studio-x7k9m2';

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await amplifyApi.getProduct(productId);
        if (response.success && response.product) {
          setProduct(response.product);
          // Use the default mockup index if set by the user
          // This should match the same image shown in the ProductGallery
          if (response.product.defaultMockupIndex && response.product.defaultMockupIndex > 0) {
            setCurrentImageIndex(response.product.defaultMockupIndex);
          }
        } else {
          setError(response.error || 'Product not found');
        }
      } catch (err) {
        console.error('Failed to fetch product:', err);
        setError('Failed to load product. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  // Get all available images - use the same logic as ProductGallery for consistency
  // The images array from Printify is the source of truth
  const getProductImages = () => {
    if (!product) return [];

    // Primary source: product.images array (same as ProductGallery uses)
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images.map(img => typeof img === 'string' ? img : img.src).filter(Boolean);
    }

    // Fallback: collect from other sources
    const images = [];
    if (product.mainImage) images.push(product.mainImage);
    if (product.mockupUrl && product.mockupUrl !== product.mainImage) images.push(product.mockupUrl);

    if (product.mockups && Array.isArray(product.mockups)) {
      product.mockups.forEach(m => {
        const url = m.mockup_url || m.src;
        if (url && !images.includes(url)) images.push(url);
      });
    }

    return images;
  };

  const images = getProductImages();

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Get price range from variants
  const getPriceRange = () => {
    if (!product?.variants || product.variants.length === 0) {
      return product?.price ? `$${product.price}` : 'Price not available';
    }

    const enabledVariants = product.variants.filter(v => v.is_enabled);
    if (enabledVariants.length === 0) {
      return product?.price ? `$${product.price}` : 'Price not available';
    }

    const prices = enabledVariants.map(v => v.price).filter(p => p != null);
    if (prices.length === 0) return 'Price not available';

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    }
    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Product Not Found</h1>
          <p className="text-slate-600 mb-6">{error || 'This product may have been removed or is no longer available.'}</p>
          <Link
            to={studioRoute}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to={studioRoute} className="flex items-center gap-2.5">
            {brand?.logo?.src ? (
              <img src={brand.logo.src} alt={brandName} className="h-9 w-auto" />
            ) : (
              <div className="relative w-9 h-9">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl rotate-3 shadow-lg shadow-blue-500/25"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </div>
              </div>
            )}
            <div className="flex flex-col">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">
                {brand?.nameParts?.main || 'Merch'}<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{brand?.nameParts?.accent || 'Like'}</span>
              </h1>
              <span className="text-[10px] font-medium text-slate-400 -mt-1 tracking-wide">{brand?.tagline || 'Merch like magic.'}</span>
            </div>
          </Link>
          <Link
            to={studioRoute}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Back to Store</span>
          </Link>
        </div>
      </header>

      {/* Product Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 lg:p-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative aspect-square bg-slate-100 rounded-xl overflow-hidden">
                {images.length > 0 ? (
                  <img
                    src={images[currentImageIndex]}
                    alt={product.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-24 h-24 text-slate-300" />
                  </div>
                )}

                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-slate-700" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-md flex items-center justify-center transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-slate-700" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            idx === currentImageIndex ? 'bg-blue-600' : 'bg-white/70 hover:bg-white'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnail Strip */}
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.slice(0, 6).map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-blue-500' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {images.length > 6 && (
                    <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 text-sm font-medium">
                      +{images.length - 6}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {product.title}
                </h1>
                <p className="text-3xl font-bold text-blue-600">
                  {getPriceRange()}
                </p>
              </div>

              {product.description && (
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-600">{product.description}</p>
                </div>
              )}

              {/* Variant count info */}
              {product.variants && product.variants.length > 0 && (
                <div className="text-sm text-slate-500">
                  {product.variants.filter(v => v.is_enabled).length} options available
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={() => setIsCheckoutOpen(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Buy Now
                </button>
                <button
                  onClick={() => setIsShareOpen(true)}
                  className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Premium Quality</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Secure Checkout</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Fast Shipping</span>
                </div>
              </div>

              {/* More from this creator */}
              {product.ownerId && brand?.features?.shop?.enabled !== false && (
                <div className="pt-4 border-t border-slate-100">
                  <Link
                    to={`/shop/${product.ownerId}`}
                    className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    <Store className="w-4 h-4" />
                    More from this creator
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-slate-500 text-sm">
            <p>Powered by {brandName}</p>
          </div>
        </div>
      </footer>

      {/* Checkout Modal */}
      <CheckoutModal
        product={product}
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onOrderComplete={() => setIsCheckoutOpen(false)}
        api={amplifyApi}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        product={product}
      />
    </div>
  );
};

export default ProductPage;
